import { useState, useEffect } from 'react';
import { UserStarIcon, Users, UserCheck, UserX, UserCog, Filter, X, RefreshCw } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';
import CustomerModal from '../../components/Modals/CustomerModal';
import api from '../../api/axios';

const CustomersPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const cusomersURL = process.env.REACT_APP_API_CUSTOMERS_URL;
  const [customers, setCustomers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [countries, setCountries] = useState([]);

  const [countriesLoading, setCountriesLoading] = useState(false);
  const [providersLoading, setProvidersLoading] = useState(false);

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [stats, setStats] = useState({});

  const [filters, setFilters] = useState({
    status: '',
    provider_id: '',
    country_id: '',
  });

  const fetchAllCountries = async () => {
    setCountriesLoading(true);
    try {
      const firstResponse = await api.get(process.env.REACT_APP_API_COUNTRY_URL);
      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allCountries = [...firstData.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(process.env.REACT_APP_API_COUNTRY_URL, {
              params: { page }
            }).then(res => res.data.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allCountries.push(...pageData);
        });
      }

      const formattedCountries = allCountries.map(country => ({
        id: country.id,
        name: country[lang] || country.en || country.fr || country.es || country.id,
        code: country.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setCountries(formattedCountries);

    } catch (error) {
      console.error('Erreur lors du chargement des pays:', error);
      toast.error(t('errorLoadingCountries'));
    } finally {
      setCountriesLoading(false);
    }
  };

  // charger tous les providers avec pagination
  const fetchAllProviders = async () => {
    setProvidersLoading(true);
    try {
      const urlBase = process.env.REACT_APP_API_PROVIDER_URL;
      const baseRequestBody = {
        search: "",
        status: ""
      };

      const firstResponse = await api.post(urlBase, baseRequestBody, {
        params: { page: 1 }
      });

      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allProviders = [...firstData.result.data];

      if (lastPage > 1) {
        const promises = [];

        for (let page = 2; page <= lastPage; page++) {
          promises.push(
            api.get(urlBase, baseRequestBody, {
              params: { page: page }
            }).then(res => res.data.result.data)
          );
        }

        const pagesResults = await Promise.all(promises);
        pagesResults.forEach(pageData => {
          allProviders.push(...pageData);
        });
      }

      const formattedProviders = allProviders.map(provider => ({
        id: provider.id,
        name: provider.name,
        email: provider.email,
        phone: provider.phone,
        country_id: provider.country_id,
        country_name: provider.country?.[lang] || provider.country?.fr || provider.country?.id
      })).sort((a, b) => a.name.localeCompare(b.name));

      setProviders(formattedProviders);


    } catch (error) {
      console.error('Erreur lors du chargement des providers:', error);
      toast.error(t('errorLoadingProviders'));
    } finally {
      setProvidersLoading(false);
    }
  };

  const fetchAllCustomers = async () => {
    try {

      const urlBase = cusomersURL;

      // Définir le body correctement
      const baseRequestBody = {
        country_id: filters.country_id,
        provider_id: filters.provider_id,
        status: filters.status
      };

      // 1. Charger la page 1
      const firstResponse = await api.get(urlBase, {
        params: { page: 1 }
      });

      const firstData = firstResponse.data;

      const lastPage = firstData.last_page;


      let allCustomers = firstData.result.data.map(formatCustomer);

      // 2. Charger les autres pages EN PARALLÈLE
      const promises = [];

      for (let page = 2; page <= lastPage; page++) {
        promises.push(
          api.get(urlBase, baseRequestBody, {
            params: { page: page }
          })
            .then(res => res.data.result.data.map(formatCustomer))
        );
      }

      const pagesResults = await Promise.all(promises);

      pagesResults.forEach(page => {
        allCustomers.push(...page);
      });

      // 3. Mettre à jour l'état
      setCustomers(allCustomers);
      setIsFilterApplied(checkIfFilterApplied());

    } catch (err) {
      console.error("Erreur API détaillée:", err);
      if (err.response) {
        console.error("Status:", err.response.status);
        console.error("Data:", err.response.data);
        console.error("Headers:", err.response.headers);
      }
    } finally {
    }
  };

  // Fonction pour formater les données de l'API
  const formatCustomer = (customer) => {
    return {
      id: customer.id,
      name: customer.name,
      country: customer.country ? customer.country.name : '',
      email: customer.email,
      address: customer.address,
      phone: customer.phone || '',
      currency: customer.currency,
      city: customer.city.name,
      status: customer.status === 1 ? 'ENABLED' : 'DISABLED'
    };
  };

  const customerColumns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: t('name') },
    { key: 'email', label: t('email') },
    { key: 'phone', label: t('phone') },
    { key: 'country', label: t('country') },
    { key: 'currency', label: t('currency') },
    { key: 'status', label: t('status') },
  ];

  // Charger les fournisseurs au montage du composant
  useEffect(() => {
    fetchAllCustomers();
    fetchAllCountries();
    fetchAllProviders();
    fetchStats();
  }, []);

  const checkIfFilterApplied = () => {
    return filters.status !== '' || filters.provider_id !== '' || filters.country_id !== '';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      provider_id: '',
      status: '',
      country_id: ''
    });
    fetchAllCustomers();
  };
  const applyFilters = () => {
    fetchAllCustomers();
  }


  const fetchStats = async () => {
    try {
      const response = await api.get(`${cusomersURL}/stats`);
      const statsData = response.data.result;
      setStats(statsData);
    }
    catch (err) {
      console.error("Error:", err);
    }
  }

  // Cartes de statistiques
  const customerStats = [
    {
      title: t('totalCustomers'),
      value: stats.total?.toString() || '0',
      description: t('allPlatformCustomers'),
      icon: Users,
      color: '#7c3aed',
    },
    {
      title: t('enabledCustomers'),
      value: stats.active?.toString() || '0',
      description: t('activeCustomers'),
      icon: UserCheck,
      color: '#10b981',
    },
    {
      title: t('disabledCustomers'),
      value: stats.inactive?.toString() || '0',
      description: t('inactiveCustomers'),
      icon: UserX,
      color: '#ef4444',
    },
    {
      title: t('pendingCustomers'),
      value: stats.pending?.toString() || '0',
      description: t('waitingCustomers'),
      icon: UserCog,
      color: '#eab308',
    }
  ];

  // Handlers pour la modal utilisateur
  const handleEditUser = (customer) => {
    console.log(customer);
    setSelectedCustomer(customer);
    setModalType('edit');
    setShowCustomerModal(true);
  };

  const handleEdit = async (customerData) => {
    try {
      const response = await api.post(`${cusomersURL}/${customerData.id}/edit`, customerData);
      toast.success(t('userUpdatedSuccess'));
    } catch (err) {
      console.error("Error:", err);
      toast.error(t('userUpdateError'));
    }
  };


  const handleDelete = async (customer) => {
    const result = await Swal.fire({
      title: t('confirmDeleteTitle'),
      text: t('confirmDeleteText', { name: customer.name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: t('deleteConfirm'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await api.post(`${cusomersURL}/${customer.id}/delete`);
        toast.success(t('userDeletedSuccess'));
      } catch (err) {
        console.error("Error:", err);
        toast.error(t('userDeleteError'));
      }
    }
  };

  const handleDetails = (customer) => {
    setSelectedCustomer(customer);
    navigate(`/customers/${customer.id}`);
  };

  // Nouveau handler pour le click sur la ligne
  const handleRowClick = (customer) => {
    handleDetails(customer);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      backgroundColor: '#7c3aed',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s ease'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '4px'
    },
    statTitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
      fontWeight: '600'
    },
    statDescription: {
      fontSize: '12px',
      color: '#9ca3af'
    },
    filtersContainer: {
      background: 'white',
      padding: '24px',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      marginBottom: '28px',
      border: '1px solid #e5e7eb'
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '20px',
      marginBottom: '20px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#374151',
      marginBottom: '6px',
      letterSpacing: '0.5px'
    },
    select: {
      padding: '12px 14px',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      fontSize: '14px',
      background: 'white',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      ':focus': {
        outline: 'none',
        borderColor: '#7c3aed',
        boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
      },
      ':hover': {
        borderColor: '#d1d5db'
      }
    },
    filterButtons: {
      display: 'flex',
      gap: '14px',
      justifyContent: 'flex-start',
      marginTop: '8px'
    },
    primaryButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(124, 58, 237, 0.2)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(124, 58, 237, 0.3)'
      }
    },
    secondaryButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontWeight: '600',
      fontSize: '14px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px rgba(107, 114, 128, 0.2)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 12px rgba(107, 114, 128, 0.3)'
      }
    },
  };

  const filterIndicatorStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: '12px',
    transition: 'all 0.3s ease',
    width: 'fit-content'
  };

  return (
    <div style={styles.container}>
      {/* Header avec titre et bouton */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <UserStarIcon size={32} color="#10b981" />
          <h1 style={styles.title}>{t('customersManagement')}</h1>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {customerStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                ...styles.statIcon,
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`
              }}>
                <Icon size={28} color="white" />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statTitle}>{stat.title}</div>
                <div style={styles.statDescription}>{stat.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.filtersContainer}>
        <div style={styles.filtersGrid}>

          {/* Filtre Fournisseur */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('provider') || 'Fournisseur'}</label>
                      {providersLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
            <select
              value={filters.provider_id}
              onChange={(e) => handleFilterChange('provider_id', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allProviders') || 'Tous les fournisseurs'}</option>
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
            )}
          </div>

          {/* Filtre Statut (existant) */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('status')}</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allStatus')}</option>
              <option value="enabled">{t('enabled')}</option>
              <option value="disabled">{t('disabled')}</option>
            </select>
          </div>

          {/* Nouveau: Filtre Pays */}
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('country') || 'Pays'}</label>
                       {countriesLoading ? (
              <div style={styles.loadingSelect}>
                <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
                {t('loading')}...
              </div>
            ) : (
            <select
              value={filters.country_id}
              onChange={(e) => handleFilterChange('country_id', e.target.value)}
              style={styles.select}
            >
              <option value="">{t('allCountries') || 'Tous les pays'}</option>
              {countries.map(country => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            )}
          </div>
        </div>


        {isFilterApplied && (
          <div style={{ ...filterIndicatorStyle, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '1px solid #fbbf24', color: '#92400e' }}>
            <Filter size={16} />
            {t('filterActive') || 'Filtre actif'}

            {[
              [t('provider'), providers.find(p => p.id === parseInt(filters.provider_id))?.name],
              [t('status'), filters.status && t(filters.status)],
              [t('country'), countries.find(c => c.id === filters.country_id)?.name],
            ].map(([label, val]) => val && ` - ${label}: ${val}`)}

            {filterLoading && <span style={{ marginLeft: '8px' }}>{t('loading')}</span>}
          </div>
        )}


        {filterLoading && !isFilterApplied && (
          <div style={{
            ...filterIndicatorStyle,
            background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
            border: '1px solid #818cf8',
            color: '#3730a3'
          }}>
            {t('loading') || 'Chargement...'}
          </div>
        )}

        <div style={styles.filterButtons}>
          <button
            style={styles.secondaryButton}
            onClick={() => {
              clearFilters();
              setIsFilterApplied(false);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(107, 114, 128, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(107, 114, 128, 0.2)';
            }}
            disabled={filterLoading}
          >
            <X size={18} />
            {t('clearFilters')}
          </button>
          <button
            style={{
              ...styles.primaryButton,
              opacity: filterLoading ? 0.7 : 1,
              cursor: filterLoading ? 'not-allowed' : 'pointer'
            }}
            onClick={applyFilters}
            onMouseEnter={(e) => {
              if (!filterLoading) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(124, 58, 237, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!filterLoading) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(124, 58, 237, 0.2)';
              }
            }}
            disabled={filterLoading}
          >
            <Filter size={18} />
            {filterLoading ? (t('loading') || 'Chargement...') : t('applyFilters')}
          </button>
        </div>
      </div>


      {/* Tableau des utilisateurs */}
      <ReusableTable
        data={customers}
        columns={customerColumns}
        onEdit={handleEditUser}
        onDelete={handleDelete}
        onDetails={handleDetails}
        onRowClick={handleRowClick}
        lang={lang}
        itemsPerPageOptions={[5, 10, 20, 50]}
        colors={{
          headerBg: '#10b981',
          buttonEdit: '#3b82f6',
          buttonDelete: '#ef4444',
          buttonDetails: '#10b981',
          rowHover: '#d9f5d7ff',
          paginatebg: '#d2f5d7ff'
        }}
      />

      {showCustomerModal && (

        <CustomerModal
          supplier={selectedCustomer}
          type={modalType}
          onClose={() => setShowCustomerModal(false)}
          onSave={(customerData) => {
            if (modalType === 'edit') {
              handleEdit(customerData);
            }
            setShowCustomerModal(false);
          }}
          lang={lang}
        />
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default CustomersPage;